/**
 * Workflow CRUD mutation hooks for the AC-1377 React Query migration
 * (AC-1402 / Phase 3.1).
 *
 * Implements the contract from the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`:
 *   - Section 2.6 — mutation + invalidation contract
 *   - Section 5.3 — mutation hook signatures
 *   - Section 8.2 — Phase 3 verification table
 *
 * ## Why this exists
 *
 * Today, `WorkflowEditor.tsx` and `WorkflowsPage.tsx` mutate workflows by
 * calling `apiService.{createWorkflow,updateWorkflow,deleteWorkflow}` directly
 * and then either reloading via the legacy `useFlowStore.loadWorkflow` shim
 * (post AC-1401, that shim invalidates RQ keys but the call site still has to
 * know to invoke it) or refetching ad hoc via `apiService.getAllWorkflows`.
 * Both paths put the invalidation contract at the call site, which is the
 * shape that produced AC-1390 / AC-1391 / AC-1394 (stale-modal-data class
 * of bugs).
 *
 * These hooks centralize the contract: every mutation knows which keys it
 * invalidates, and call sites get atomic "mutate and the relevant subscribers
 * automatically refresh" semantics. The legacy direct-`apiService` paths in
 * `WorkflowEditor` are NOT migrated by this ticket — that's AC-1406. AC-1404
 * (workflow rename optimistic) and AC-1405 (step drag optimistic) extend the
 * mutation surface with `onMutate` snapshots; this ticket is intentionally
 * pessimistic.
 *
 * ## Behavior
 *
 * All three mutations follow the same pattern:
 *   - `mutationFn` calls the corresponding `apiService` method.
 *   - On success (`onSuccess`), `queryClient.invalidateQueries` is called
 *     for each key in TD section 2.6's invalidation table.
 *   - On error, RQ surfaces `isError` and `error` to the caller; we do NOT
 *     fire any invalidation. Cache must not be perturbed by a failed write.
 *   - Returns the standard `UseMutationResult` so consumers can use
 *     `mutate`, `mutateAsync`, `isPending`, `isError`, `error`, `data`.
 *
 * ## Why no closure ID for these hooks
 *
 * Workflow CRUD mutations are not scoped to a single workflow at hook
 * construction time the way step mutations are (steps live "inside" a known
 * workflow). For workflow CRUD the ID arrives at `mutate(...)` time:
 *
 *   - `useCreateWorkflow()` doesn't have an ID yet (server assigns one).
 *   - `useUpdateWorkflow()` is invoked from contexts that may operate on
 *     different workflows (e.g. a list page with inline rename buttons).
 *   - `useDeleteWorkflow()` likewise.
 *
 * Keeping the args at `mutate(...)` time makes the call-site shape uniform
 * across the three hooks and matches the TD signatures exactly. Step
 * mutations DO take `workflowId` at hook level — see `useStepMutations.ts`
 * for the rationale.
 *
 * ## Cache eviction on delete
 *
 * `useDeleteWorkflow` does TWO things on success:
 *   1. `queryClient.removeQueries({ queryKey: queryKeys.workflow(id) })` —
 *      evicts the workflow detail (and, by prefix match, its steps and
 *      execution data) from the cache. This is important because after a
 *      delete, the data is gone server-side; we don't want a future remount
 *      to flash cached-then-empty before the 404 lands.
 *   2. `queryClient.invalidateQueries({ queryKey: queryKeys.workflows() })`
 *      — marks the all-workflows list stale so any active subscriber
 *      (Dashboard, WorkflowsPage) refetches and re-renders without the
 *      deleted item.
 */

import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import apiService from '../../services/api';
import { CreateWorkflowForm, Workflow } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * Create-workflow input. The caller passes the projectId (which scopes the
 * server-side endpoint) and the form data; the server returns the created
 * workflow with its assigned id.
 */
export type CreateWorkflowVariables = {
  projectId: string;
  data: CreateWorkflowForm;
};

/**
 * Update-workflow input. The workflowId is supplied at mutate-time (not at
 * hook level) so a single hook instance can serve multiple workflow edits
 * without re-mounting. `data` is the partial form payload — full TS type
 * widening (e.g. start_node_position_x) lands in AC-1404 / AC-1405's
 * optimistic mutations.
 */
export type UpdateWorkflowVariables = {
  workflowId: string | number;
  data: Partial<CreateWorkflowForm> & {
    start_node_position_x?: number;
    start_node_position_y?: number;
  };
};

/**
 * Delete-workflow input. workflowId only — the call site does not need to
 * supply the projectId; the server resolves project from the workflow row.
 */
export type DeleteWorkflowVariables = {
  workflowId: string | number;
};

/**
 * AC-1598 / AC-1594: Copy-workflow input.
 *
 * `workflowId` is the source workflow id; the payload matches the backend's
 * `OrchestrationCopyRequest` schema: `{ name, target_project_id? }`. A null
 * or omitted `target_project_id` is treated as "copy within the source
 * project" — but per TD lines 355-367 the hook only invalidates project-
 * scoped keys when `target_project_id` is non-null (we cannot derive the
 * source project from the mutation variables alone).
 */
export type CopyWorkflowVariables = {
  workflowId: string | number;
  payload: {
    name: string;
    target_project_id?: number | null;
  };
};

/**
 * `useCreateWorkflow()` — mutation hook for `POST /api/v1/orchestrations/project/:projectId`.
 *
 * Invalidates `queryKeys.workflows()` on success so list views refresh.
 *
 * @example
 *   const create = useCreateWorkflow();
 *   const onSubmit = async (form) => {
 *     const created = await create.mutateAsync({ projectId, data: form });
 *     navigate(`/workflows/${created.orchestration_id}`);
 *   };
 */
export function useCreateWorkflow(): UseMutationResult<
  Workflow,
  Error,
  CreateWorkflowVariables
> {
  const queryClient = useQueryClient();
  return useMutation<Workflow, Error, CreateWorkflowVariables>({
    mutationFn: ({ projectId, data }) =>
      apiService.createWorkflow(projectId, data),
    onSuccess: () => {
      // Only the list key changes — the new workflow's detail key has no
      // active subscribers yet (the caller will navigate to it next, at
      // which point `useWorkflow(newId)` cold-fetches).
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows() });
    },
  });
}

/**
 * `useUpdateWorkflow()` — mutation hook for `PUT /api/v1/orchestrations/:id`.
 *
 * Invalidates BOTH `queryKeys.workflow(id)` (the detail entry — and, by
 * prefix match, its steps/execution sub-keys) AND `queryKeys.workflows()`
 * (the list, since list rows often render derived fields like `name` or
 * `status` that the update may have changed).
 *
 * @example
 *   const update = useUpdateWorkflow();
 *   await update.mutateAsync({ workflowId: 1, data: { name: 'Renamed' } });
 */
export function useUpdateWorkflow(): UseMutationResult<
  Workflow,
  Error,
  UpdateWorkflowVariables
> {
  const queryClient = useQueryClient();
  return useMutation<Workflow, Error, UpdateWorkflowVariables>({
    // `apiService.updateWorkflow` types its first arg as `string`, but our
    // public API accepts `string | number` to match TD section 5.3 and the
    // `queryKeys` factory's coercion contract. The runtime call works for
    // both because `updateWorkflow` URL-interpolates. The cast is removed in
    // AC-1401's typing pass on apiService (already widened in queryKeys).
    mutationFn: ({ workflowId, data }) =>
      apiService.updateWorkflow(workflowId as string, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflow(variables.workflowId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows() });
    },
  });
}

/**
 * `useDeleteWorkflow()` — mutation hook for `DELETE /api/v1/orchestrations/:id`.
 *
 * On success: removes the workflow's detail entry from cache (so the gone
 * data does not flash on remount) and invalidates the list so subscribers
 * re-render without the deleted item.
 *
 * @example
 *   const remove = useDeleteWorkflow();
 *   if (confirm('Delete?')) await remove.mutateAsync({ workflowId: id });
 */
export function useDeleteWorkflow(): UseMutationResult<
  void,
  Error,
  DeleteWorkflowVariables
> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, DeleteWorkflowVariables>({
    mutationFn: ({ workflowId }) =>
      apiService.deleteWorkflow(workflowId as string),
    onSuccess: (_data, variables) => {
      // Evict the detail entry. RQ's prefix-match means this ALSO removes
      // ['workflow', id, 'steps'] and ['workflow', id, 'execution'] from
      // the cache — see queryKeys.ts JSDoc for the hierarchical contract.
      queryClient.removeQueries({
        queryKey: queryKeys.workflow(variables.workflowId),
      });
      // Mark the list stale so any active subscriber refetches.
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows() });
    },
  });
}

/**
 * `useCopyWorkflow()` — mutation hook for AC-1598 / AC-1594's
 * `POST /api/v1/orchestrations/:id/copy` endpoint.
 *
 * On success, invalidates:
 *   - `queryKeys.workflows()` — cross-project workflows list refreshes so the
 *     new row appears wherever the list is subscribed.
 *   - `queryKeys.projectAgents(target_project_id)` and
 *     `queryKeys.projectModels(target_project_id)` — but ONLY when
 *     `target_project_id` is non-null. A cross-project copy clones agents
 *     AND models into the target project (TD lines 633-700); same-project
 *     copies harmlessly refetch the source project's keys (the contract is
 *     simply "if target_project_id is non-null, invalidate that project's
 *     agents and models" — see TD lines 348-368).
 *
 * On failure (e.g. 409 name collision), `isError` is set and NO invalidation
 * fires — the cache must not be perturbed by a failed write.
 *
 * @example
 *   const copy = useCopyWorkflow();
 *   const result = await copy.mutateAsync({
 *     workflowId: source.orchestration_id,
 *     payload: { name: `${source.name} (copy)`, target_project_id: 42 },
 *   });
 *   navigate(`/projects/${result.project_id}/workflows/${result.orchestration_id}`);
 */
export function useCopyWorkflow(): UseMutationResult<
  Workflow,
  Error,
  CopyWorkflowVariables
> {
  const queryClient = useQueryClient();
  return useMutation<Workflow, Error, CopyWorkflowVariables>({
    mutationFn: ({ workflowId, payload }) =>
      apiService.copyWorkflow(workflowId, payload),
    onSuccess: (_data, variables) => {
      // Always: the cross-project workflows list refreshes.
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows() });

      // Only when target_project_id is supplied: refresh that project's
      // agents and models. The hook does NOT know the source project id
      // from the variables alone, so it cannot invalidate the source
      // project's keys when target_project_id is null — that case yields a
      // no-op for project-scoped keys, by design (TD lines 355-367).
      const targetProjectId = variables.payload.target_project_id;
      if (targetProjectId != null) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projectAgents(targetProjectId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.projectModels(targetProjectId),
        });
      }
    },
  });
}
